import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { daysUntilDate, formatDateOnly, statusLabel, statusTone } from "@alm/domain";
import type { FlagTone } from "@/components/app/clinical/clinical-flag";
import { PageHeader } from "@/components/app/shell/page-header";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { groupByPerson, HrTodoCard, reportLabel } from "./-components/hr-todo-card";
import { ReportLink } from "@/routes/_authenticated/_workforce/hr/-components/report-link";

export const Route = createFileRoute("/_authenticated/_workforce/hr/overview")({
  component: HrOverview,
});

/* A missing or rejected document is owed now; one waiting for review is not
   the employee's to fix, so it stays quiet. */
const missingTone = (status: string): FlagTone => {
  const tone = statusTone(status);
  return tone === "neutral" ? "idle" : tone === "ok" ? "idle" : tone;
};

const people = (count: number) => `${count} ${count === 1 ? "person" : "people"}`;

function lapseLabel(expiresAt: string | null): { label: string; tone: FlagTone } {
  const days = daysUntilDate(expiresAt);
  if (days === null) return { label: "No date", tone: "idle" };
  if (days <= 0) return { label: "Expires today", tone: "crit" };
  if (days === 1) return { label: "Tomorrow", tone: "crit" };
  return { label: `In ${days} days`, tone: days <= 30 ? "warn" : "idle" };
}

/** The compliance view. A count stays "-" until its report is read: a zero from a failed read would
 * make an administrator believe the files are complete. */
function HrOverview() {
  const { facilityId } = useFacility();
  const missing = useQuery(
    orpc.workforce.employeeDocuments.missingReport.queryOptions({ input: { facilityId } }),
  );
  const expiring = useQuery(
    orpc.workforce.employeeDocuments.expiringReport.queryOptions({
      input: { facilityId, days: 60 },
    }),
  );
  // Soonest first: the renewal that lapses next is the one to chase today.
  const lapsing = (expiring.data ?? []).toSorted((left, right) =>
    (left.expiresAt ?? "").localeCompare(right.expiresAt ?? ""),
  );
  // Lapsed and turned-down first: they are already a survey finding.
  const owed = (missing.data ?? []).toSorted(
    (left, right) =>
      Number(missingTone(right.status) === "crit") - Number(missingTone(left.status) === "crit"),
  );
  const owing = groupByPerson(
    owed,
    (row) => ({
      key: row.requirementId,
      document: row.requirement,
      label: statusLabel(row.status),
      tone: missingTone(row.status),
    }),
    (row) => missingTone(row.status) === "crit",
  );
  const renewing = groupByPerson(
    lapsing,
    (row) => ({
      key: row.requirementId,
      document: row.requirement,
      ...lapseLabel(row.expiresAt),
      title: row.expiresAt ? `Expires ${formatDateOnly(row.expiresAt)}` : undefined,
    }),
    (row) => (daysUntilDate(row.expiresAt) ?? 99) <= 7,
  );
  const meta =
    missing.data && expiring.data
      ? missing.data.length + lapsing.length === 0
        ? "Every required document is on file and nothing lapses in the next 60 days."
        : `${people(owing.length)} ${owing.length === 1 ? "owes" : "owe"} documents, ${people(renewing.length)} renewing within 60 days`
      : "The documents employees have to hold, and what is missing or about to lapse.";

  return (
    <>
      <PageHeader title="Documents" meta={meta} />

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <HrTodoCard
          title="Missing or expired"
          hint="Required documents not on file today"
          tone="crit"
          count={missing.data?.length}
          people={owing}
          query={missing}
          failedMessage="Could not load the missing-document report. This is not a statement that everybody is compliant."
          empty={{
            title: "Nothing outstanding",
            description: "Every required document is on file and current.",
          }}
          report={
            <ReportLink>
              <Link to="/hr/missing-documents">{reportLabel(owing, "Open missing documents")}</Link>
            </ReportLink>
          }
        />
        <HrTodoCard
          title="Expiring soon"
          hint="Accepted documents lapsing within 60 days"
          tone="warn"
          count={expiring.data?.length}
          people={renewing}
          query={expiring}
          failedMessage="Could not load the expiring-document report. Nothing here means nothing was read, not that nothing lapses."
          empty={{
            title: "Nothing lapsing",
            description: "No accepted document expires in the next 60 days.",
          }}
          report={
            <ReportLink>
              <Link to="/hr/expiring-documents" search={{ days: 60 }}>
                {reportLabel(renewing, "Open expiring documents")}
              </Link>
            </ReportLink>
          }
        />
      </div>
    </>
  );
}
